import { useParams, Navigate } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { SEOHead } from "@/hooks/useSEO";
import seoConfig from "@/config/seo.json";
import {
  StructuredDataHead,
  LLMMetadataHead,
  organizationSchema,
  createWebPageSchema,
} from "@/hooks/useStructuredData";
import { getIndustryPageBySlug } from "@/data/industryPages";
import { useLanguage } from "@/hooks/useLanguage";

// Maschinenbau Components
import MaschinenbauHero from "@/components/landing/maschinenbau/MaschinenbauHero";
import HerausforderungenSection from "@/components/landing/maschinenbau/HerausforderungenSection";
import LoesungSection from "@/components/landing/maschinenbau/LoesungSection";
import VorteileSection from "@/components/landing/maschinenbau/VorteileSection";
import ChecklistSection from "@/components/landing/maschinenbau/ChecklistSection";
import FAQMaschinenbauSection from "@/components/landing/maschinenbau/FAQMaschinenbauSection";
import CTASection from "@/components/landing/CTASection";

// Versicherung Components
import VersicherungHero from "@/components/landing/versicherung/VersicherungHero";
import HerausforderungenVersicherungSection from "@/components/landing/versicherung/HerausforderungenVersicherungSection";
import LoesungVersicherungSection from "@/components/landing/versicherung/LoesungVersicherungSection";
import VorteileVersicherungSection from "@/components/landing/versicherung/VorteileVersicherungSection";
import ChecklistVersicherungSection from "@/components/landing/versicherung/ChecklistVersicherungSection";
import FAQVersicherungSection from "@/components/landing/versicherung/FAQVersicherungSection";

// Einzelhandel Components
import EinzelhandelHero from "@/components/landing/einzelhandel/EinzelhandelHero";
import HerausforderungenEinzelhandelSection from "@/components/landing/einzelhandel/HerausforderungenEinzelhandelSection";
import LoesungEinzelhandelSection from "@/components/landing/einzelhandel/LoesungEinzelhandelSection";
import VorteileEinzelhandelSection from "@/components/landing/einzelhandel/VorteileEinzelhandelSection";
import ChecklistEinzelhandelSection from "@/components/landing/einzelhandel/ChecklistEinzelhandelSection";
import FAQEinzelhandelSection from "@/components/landing/einzelhandel/FAQEinzelhandelSection";

// IT & Software Components
import ITSoftwareHero from "@/components/landing/it-software/ITSoftwareHero";
import HerausforderungenITSection from "@/components/landing/it-software/HerausforderungenITSection";
import LoesungITSection from "@/components/landing/it-software/LoesungITSection";
import VorteileITSection from "@/components/landing/it-software/VorteileITSection";
import ChecklistITSection from "@/components/landing/it-software/ChecklistITSection";
import FAQITSection from "@/components/landing/it-software/FAQITSection";

// E-Commerce Components
import ECommerceHero from "@/components/landing/e-commerce/ECommerceHero";
import HerausforderungenECommerceSection from "@/components/landing/e-commerce/HerausforderungenECommerceSection";
import LoesungECommerceSection from "@/components/landing/e-commerce/LoesungECommerceSection";
import VorteileECommerceSection from "@/components/landing/e-commerce/VorteileECommerceSection";
import ChecklistECommerceSection from "@/components/landing/e-commerce/ChecklistECommerceSection";
import FAQECommerceSection from "@/components/landing/e-commerce/FAQECommerceSection";

// Tourismus Components
import TourismusHero from "@/components/landing/tourismus/TourismusHero";
import HerausforderungenTourismusSection from "@/components/landing/tourismus/HerausforderungenTourismusSection";
import LoesungTourismusSection from "@/components/landing/tourismus/LoesungTourismusSection";
import VorteileTourismusSection from "@/components/landing/tourismus/VorteileTourismusSection";
import ChecklistTourismusSection from "@/components/landing/tourismus/ChecklistTourismusSection";
import FAQTourismusSection from "@/components/landing/tourismus/FAQTourismusSection";

// Verbände Components
import VerbaendeHero from "@/components/landing/verbaende/VerbaendeHero";
import HerausforderungenVerbaendeSection from "@/components/landing/verbaende/HerausforderungenVerbaendeSection";
import LoesungVerbaendeSection from "@/components/landing/verbaende/LoesungVerbaendeSection";
import VorteileVerbaendeSection from "@/components/landing/verbaende/VorteileVerbaendeSection";
import ChecklistVerbaendeSection from "@/components/landing/verbaende/ChecklistVerbaendeSection";
import FAQVerbaendeSection from "@/components/landing/verbaende/FAQVerbaendeSection";

type PageKey = keyof typeof seoConfig.pages;

const IndustryDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const pageData = slug ? getIndustryPageBySlug(slug) : undefined;

  if (!pageData) {
    return <Navigate to="/404" replace />;
  }

  const renderContent = () => {
    switch (pageData.componentSet) {
      case "maschinenbau":
        return (<><MaschinenbauHero /><HerausforderungenSection /><LoesungSection /><VorteileSection /><ChecklistSection /><FAQMaschinenbauSection /><CTASection /></>);
      case "versicherung":
        return (<><VersicherungHero /><HerausforderungenVersicherungSection /><LoesungVersicherungSection /><VorteileVersicherungSection /><ChecklistVersicherungSection /><FAQVersicherungSection /><CTASection /></>);
      case "einzelhandel":
        return (<><EinzelhandelHero /><HerausforderungenEinzelhandelSection /><LoesungEinzelhandelSection /><VorteileEinzelhandelSection /><ChecklistEinzelhandelSection /><FAQEinzelhandelSection /><CTASection /></>);
      case "it-software":
        return (<><ITSoftwareHero /><HerausforderungenITSection /><LoesungITSection /><VorteileITSection /><ChecklistITSection /><FAQITSection /><CTASection /></>);
      case "e-commerce":
        return (<><ECommerceHero /><HerausforderungenECommerceSection /><LoesungECommerceSection /><VorteileECommerceSection /><ChecklistECommerceSection /><FAQECommerceSection /><CTASection /></>);
      case "tourismus":
        return (<><TourismusHero /><HerausforderungenTourismusSection /><LoesungTourismusSection /><VorteileTourismusSection /><ChecklistTourismusSection /><FAQTourismusSection /><CTASection /></>);
      case "verbaende":
        return (<><VerbaendeHero /><HerausforderungenVerbaendeSection /><LoesungVerbaendeSection /><VorteileVerbaendeSection /><ChecklistVerbaendeSection /><FAQVerbaendeSection /><CTASection /></>);
      default:
        return <Navigate to="/404" replace />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page={(pageData.seoKey || "home") as PageKey} locale={language} />
      <StructuredDataHead schemas={[
        organizationSchema,
        createWebPageSchema(
          pageData.title,
          pageData.description,
          `/branchen/${pageData.slug}`,
          [
            { name: "Home", url: "/" },
            { name: "Branchen", url: "/" },
            { name: pageData.breadcrumbLabel, url: `/branchen/${pageData.slug}` },
          ]
        ),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: pageData.serviceName,
          description: pageData.serviceDescription,
          provider: organizationSchema,
          serviceType: "AI Customer Service",
          areaServed: { "@type": "Country", name: "Germany" },
        },
      ]} />
      <LLMMetadataHead
        title={pageData.title}
        description={pageData.description}
        keywords={pageData.keywords}
        canonicalUrl={pageData.canonicalUrl}
        aiCategory="SaaS, AI, Branchenlösung"
        aiEntityType="Industry Solution Page"
        aiIndustry={pageData.breadcrumbLabel}
        aiUseCase={pageData.serviceDescription}
      />
      <Header />
      <main>{renderContent()}</main>
      <Footer />
    </div>
  );
};

export default IndustryDetail;
